import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import {
  getWebsiteRedirects,
  updateWebsiteRedirects,
  type WebsiteRedirectOrganizationScope,
} from './website-redirects.api'
import type {
  UpdateWebsiteRedirectsInput,
  WebsiteRedirectConfiguration,
} from './website-redirects.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries the fetcher reads; the scoped key builder below sets them. */
type WebsiteRedirectConfigurationQueryKey = [AdminQueryRoot & WebsiteRedirectOrganizationScope]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const websiteRedirectQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'website-redirects' }, { organizationId }),
  ],
  configuration: ({
    organizationId,
  }: OrganizationScope = {}): WebsiteRedirectConfigurationQueryKey =>
    buildScopedAdminQueryKey(websiteRedirectQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'configuration',
    }),
}

export const websiteRedirectQueries = {
  configuration: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: websiteRedirectQueryKeys.configuration({ organizationId }),
      queryFn: getWebsiteRedirects,
      initialData: () =>
        queryClient.getQueryData<WebsiteRedirectConfiguration>(
          websiteRedirectQueryKeys.configuration({ organizationId })
        ),
      initialDataUpdatedAt: () =>
        queryClient.getQueryState(websiteRedirectQueryKeys.configuration({ organizationId }))
          ?.dataUpdatedAt,
    }),
}

export function useWebsiteRedirects({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof websiteRedirectQueries.configuration>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...websiteRedirectQueries.configuration({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useUpdateWebsiteRedirectsMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: UpdateWebsiteRedirectsInput) =>
      updateWebsiteRedirects({ organizationId, values }),
    onSuccess: (data) => {
      queryClient.setQueryData(websiteRedirectQueryKeys.configuration({ organizationId }), data)
    },
  })
}
